import React, { useState, useRef, useEffect, useCallback, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { useSelector, useDispatch } from 'react-redux';
import classnames from 'classnames';
import { withRouter } from 'react-router-dom';
import { Icon, Button } from '@iq/react-components';
import 'react-dates/initialize';
import 'react-dates/lib/css/_datepicker.css';

import { DatePicker, TimePicker } from '../DateTimePicker';
import {
  setPollingDateRange,
  getPollingDateRange,
  getTimezone,
  periods,
  getRangeFromLabel,
  getRangeFromPartial,
  getInitialPollingDateRange,
  useDateSubscription,
} from '../../bundles/application';
import {
  utcToSite,
  siteToUtc,
  datetimeString,
  getSiteTime,
  zonedDateAndTimeToUtc,
} from '../../datetimeUtils';

const APPLY_BUTTON_ID = 'date-range-apply';

const SelectPollingDateRange = ({
  history,
  panelId,
  pickerId,
  enableGlobalDates = true,
  onRangeCommit,
  // When true, portals the dropdown into #global-modal-container with a
  // JS-computed fixed position instead of position: absolute, so it isn't
  // clipped by an ancestor with overflow: hidden (e.g. a fixed-height Section).
  escapeOverflow = false,
}) => {
  const dispatch = useDispatch();
  const internalId = pickerId || panelId;

  // Create subscription for this panel to ensure it has its own Redux state entry
  // This prevents fallback to global and enables panel independence
  // Pass enableGlobalDates to control initialization source
  useDateSubscription(panelId, enableGlobalDates);

  const externalDateRange = useSelector((state) => getPollingDateRange(state, panelId));
  const internalDateRange = useSelector((state) => getPollingDateRange(state, internalId));
  const timezone = useSelector(getTimezone);
  const [open, setOpen] = useState(false);
  const [canSave, setCanSave] = useState(false);
  const [global, setGlobal] = useState(false);
  const [localDateRange, setLocalDateRange] = useState(externalDateRange);
  const [displayDate, setDisplayDate] = useState(externalDateRange);

  const ref = useRef(null);
  const contentRef = useRef(null);
  const dateInputReference = useRef(null);
  const startTimeSecondsReference = useRef(null);
  const startTimeHoursReference = useRef(null);
  const endTimeHoursReference = useRef(null);
  const localDateRangeReference = useRef(null);
  const [contentPosition, setContentPosition] = useState(null);

  const START_DATE = `polling-range-start_${internalId}`;
  const START_TIME_SECONDS = `polling-range-start-time-s_${internalId}`;
  const START_TIME_HOURS = `polling-range-start-time-h_${internalId}`;
  const END_DATE = `polling-range-end_${internalId}`;
  const END_TIME_HOURS = `polling-range-end-time-h_${internalId}`;

  const updateLocalDateRange = useCallback((range) => {
    localDateRangeReference.current = { ...range };
    setLocalDateRange(() => range);
  }, []);

  const validateDates = useCallback(
    (defaultTo = 'start') => {
      const { startDate, endDate } = localDateRangeReference.current || {};
      if (startDate > endDate) {
        const date = defaultTo === 'start' ? startDate : endDate;
        updateLocalDateRange({
          startDate: date,
          endDate: date,
        });
      }
    },
    [updateLocalDateRange]
  );

  const handleClickOutside = useCallback(
    (event) => {
      if (!ref.current) return;
      const isInside =
        ref.current.contains(event.target) ||
        (escapeOverflow && contentRef.current?.contains(event.target));
      if (
        isInside &&
        !(
          event.target.classList.contains('DateInput_input') ||
          event.target.id.includes('polling-range') ||
          event.target.classList.contains('css-12a3bhy-singleValue')
        )
      ) {
        validateDates();
      }
      if (
        !isInside &&
        !event.target.classList.contains('CalendarDay') &&
        !event.target.classList.contains('time-picker__option')
      ) {
        validateDates();
        setOpen(false);
      }
    },
    [escapeOverflow, validateDates]
  );

  const handleOpen = useCallback((event) => {
    if (event.target === ref.current && (event.key === ' ' || event.code === 'Space')) {
      setOpen(true);
    }
  }, []);

  useEffect(() => {
    const unlisten = history.listen(() => {
      setOpen(false);
    });
    document.addEventListener('click', handleClickOutside);
    document.addEventListener('keyup', handleOpen);
    document.querySelector('#global-modal-container').addEventListener('click', handleClickOutside);
    document.querySelector('#global-modal-container').addEventListener('keyup', handleOpen);
    return () => {
      unlisten();
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('keyup', handleOpen);
      document
        .querySelector('#global-modal-container')
        .removeEventListener('click', handleClickOutside);
      document.querySelector('#global-modal-container').removeEventListener('keyup', handleOpen);
    };
  }, [history, handleClickOutside, handleOpen]);

  useEffect(() => {
    updateLocalDateRange(internalDateRange);
    setDisplayDate(() => internalDateRange);
  }, [internalDateRange, updateLocalDateRange]);

  useEffect(() => {
    updateLocalDateRange(externalDateRange);
    setDisplayDate(() => externalDateRange);
  }, [externalDateRange, updateLocalDateRange]);

  // Anchors the portaled dropdown to the trigger's current position. Closes
  // instead of repositioning if the trigger scrolls out of view, rather than
  // leaving the dropdown floating with nothing to anchor to.
  const computeContentPosition = useCallback(() => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const triggerIsVisible =
      rect.bottom > 0 &&
      rect.top < window.innerHeight &&
      rect.right > 0 &&
      rect.left < window.innerWidth;

    if (!triggerIsVisible) {
      setOpen(false);
      return;
    }

    setContentPosition({
      top: rect.bottom,
      right: window.innerWidth - rect.right,
    });
  }, []);

  useLayoutEffect(() => {
    if (!escapeOverflow || !open) return;
    computeContentPosition();

    // capture: true also catches scroll on nested scrollable ancestors (not
    // just window), since scroll doesn't bubble. rAF-throttled since scroll
    // fires far more often than the browser repaints.
    let frame = null;
    const handleReposition = () => {
      if (frame !== null) return;
      frame = requestAnimationFrame(() => {
        frame = null;
        computeContentPosition();
      });
    };

    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);
    return () => {
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }, [escapeOverflow, open, computeContentPosition]);

  const setFocus = (id, selectionStart = 0, selectionEnd = 4) => {
    const input = document.querySelector(`#${id}`);
    if (id === START_TIME_SECONDS && startTimeSecondsReference.current) {
      startTimeSecondsReference.current.focus();
    } else if (id === START_TIME_HOURS && startTimeHoursReference.current) {
      startTimeHoursReference.current.focus();
    } else if (id === END_TIME_HOURS && endTimeHoursReference.current) {
      endTimeHoursReference.current.focus();
    } else if (input) {
      input.focus();
    }
    if (input?.setSelectionRange) {
      input.setSelectionRange(selectionStart, selectionEnd);
    }
  };

  const handleFieldChange = (currentType, nextId, selectionStart = 0, selectionEnd = 4) => {
    validateDates(currentType);
    if (nextId) {
      setFocus(nextId, selectionStart, selectionEnd);
    }
  };

  const onExit = useCallback(
    (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
        updateLocalDateRange(externalDateRange);
      }
    },
    [updateLocalDateRange, externalDateRange]
  );

  useEffect(() => {
    if (open && canSave) {
      document.addEventListener('keyup', onExit);
    } else {
      document.removeEventListener('keyup', onExit);
    }
    return () => {
      document.removeEventListener('keyup', onExit);
    };
  }, [open, canSave, onExit]);

  const handleSubmitDateRange = useCallback(() => {
    // safe guard against invalid range
    if (
      localDateRangeReference.current &&
      localDateRangeReference.current.startDate <= localDateRangeReference.current.endDate
    ) {
      const range = getRangeFromPartial(localDateRangeReference.current);
      if (global) {
        dispatch(setPollingDateRange(range));
      } else {
        dispatch(setPollingDateRange(range, internalId));
      }
      onRangeCommit?.(range);

      setOpen(false);
    }
  }, [global, dispatch, internalId, onRangeCommit]);

  const handleResetDateRange = () => {
    const resetRange = global
      ? getInitialPollingDateRange()
      : getInitialPollingDateRange(internalId);

    if (global) {
      dispatch(setPollingDateRange(resetRange));
    } else {
      dispatch(setPollingDateRange(resetRange, internalId));
    }
    onRangeCommit?.(resetRange);
    setOpen(false);
  };

  const handleSubmitRelativePeriod = (period) => {
    const range = getRangeFromLabel(period);
    if (global) {
      dispatch(setPollingDateRange(range));
    } else {
      dispatch(setPollingDateRange(range, internalId));
    }
    onRangeCommit?.(range);
    setOpen(false);
  };

  const onSave = useCallback(
    (event) => {
      if (event.key === 'Enter' && event.target.id === APPLY_BUTTON_ID) {
        handleSubmitDateRange();
      }
    },
    [handleSubmitDateRange]
  );

  useEffect(() => {
    if (canSave) {
      document.addEventListener('keyup', onSave);
    } else {
      document.removeEventListener('keyup', onSave);
    }
    return () => {
      document.removeEventListener('keyup', onSave);
    };
  }, [canSave, onSave]);

  const classes = classnames('select-period-component', { open });

  const setTime = (zonedTime, type) => {
    const localDateTimes = { ...localDateRangeReference.current };
    localDateTimes[type] = zonedDateAndTimeToUtc(
      localDateTimes[type],
      zonedTime,
      timezone
    ).getTime();

    updateLocalDateRange({
      startDate: localDateTimes.startDate,
      endDate: localDateTimes.endDate,
    });
  };

  const setDate = (zonedTimestamp, type) => {
    const updateTs = siteToUtc(new Date(zonedTimestamp), timezone).getTime();

    if (type === 'startDate') {
      updateLocalDateRange({
        startDate: updateTs,
        endDate: localDateRangeReference.current?.endDate,
      });
    } else {
      updateLocalDateRange({
        startDate: localDateRangeReference.current?.startDate,
        endDate: updateTs,
      });
    }
  };

  const format = (timestamp, withTz = false) =>
    timestamp ? datetimeString(utcToSite(timestamp, timezone), timezone, false, withTz) : '-';

  const toggleOpenFromKeyboard = (event) => {
    if (event.key === 'Enter' || event.key === ' ' || event.code === 'Space') {
      event.preventDefault();
      setOpen(!open);
    }
  };

  const selectRelativePeriodFromKeyboard = (event, label) => {
    if (event.key === 'Enter' || event.key === ' ' || event.code === 'Space') {
      event.preventDefault();
      handleSubmitRelativePeriod(label);
    }
  };

  const startTime = localDateRange.startDate
    ? getSiteTime(localDateRange.startDate, timezone)
    : '00:00:00';
  const endTime = localDateRange.endDate
    ? getSiteTime(localDateRange.endDate, timezone)
    : '00:00:00';

  const pickerSpaceLeft =
    (!!dateInputReference.current && dateInputReference.current.getBoundingClientRect().left) || 0;
  const pickerClass = `absolute-time-picker ${pickerSpaceLeft < 330 ? 'calendar-right' : ''}`;
  const checkboxIcon = `check-box${global ? '' : '-outline-blank'}`;

  if (!localDateRange) return null;

  const headerNode = (
    <div
      data-testid="date-range-header"
      className="header"
      onClick={() => setOpen(!open)}
      onKeyUp={toggleOpenFromKeyboard}
      role="button"
      tabIndex={0}
    >
      <div className="label">
        {displayDate.label || null}
        {!displayDate.label && (
          <div>
            {format(displayDate.startDate)}
            <span> - </span>
            {format(displayDate.endDate)}
          </div>
        )}
      </div>
      <Icon
        icon="he-date-time"
        size="s"
      />
    </div>
  );

  // Anchored via CSS (position: absolute; right: 0; bottom: 0) when rendered
  // in place, or via this JS-computed fixed position when escapeOverflow
  // portals it into #global-modal-container.
  const contentStyle =
    escapeOverflow && contentPosition
      ? {
          position: 'fixed',
          top: contentPosition.top,
          right: contentPosition.right,
          bottom: 'auto',
          transform: 'none',
          width: 'auto',
        }
      : undefined;

  const contentNode = (
    <div
      className={classnames('select-period-content', { open })}
      ref={contentRef}
      style={contentStyle}
    >
      <div className="actions">
        <div className="actions-title">
          <p>Range Picker</p>
        </div>
        {!!localDateRange.startDate && (
          <>
            <div className="reset">
              <Button
                activity="secondary"
                slim
                type="button"
                onClick={handleResetDateRange}
                id={'date-range-reset'}
              >
                Reset
              </Button>
            </div>
            <div className="submit">
              <Button
                activity="primary"
                slim
                type="button"
                onClick={handleSubmitDateRange}
                id={APPLY_BUTTON_ID}
                disabled={localDateRange.startDate > localDateRange.endDate}
              >
                Apply
              </Button>
            </div>
          </>
        )}
      </div>
      <hr />
      {enableGlobalDates && (
        <>
          <div className="actions checkbox">
            <div className="actions-checkbox">
              <Icon
                style={{ cursor: 'pointer', color: '#3366ff' }}
                icon={checkboxIcon}
                onClick={(event) => {
                  event.stopPropagation();
                  setGlobal(!global);
                }}
              />
              <p>Use selected range across site data</p>
            </div>
          </div>

          <hr />
        </>
      )}
      <div className="absolute">
        <div className="title">Absolute range</div>
        <div
          className={pickerClass}
          ref={dateInputReference}
        >
          <label
            htmlFor={START_DATE}
            className="absolute-label"
          >
            From
          </label>
          <div className="datetime-row">
            <DatePicker
              id={START_DATE}
              value={utcToSite(localDateRange.startDate, timezone)}
              onChange={(date) => setDate(date, 'startDate')}
              onCalendarOpen={(isOpen) => setCanSave(!isOpen)}
              onTabOrArrowBack={() => handleFieldChange('start', null)}
              onTextComplete={() => handleFieldChange('start', START_TIME_HOURS)}
            />
            <TimePicker
              withSeconds
              value={startTime}
              hourInputRef={startTimeHoursReference}
              secondsInputRef={startTimeSecondsReference}
              onChange={(time) => setTime(time, 'startDate')}
              onTabOrArrowBack={() => handleFieldChange('start', START_DATE, 8, 10)}
              onTextComplete={() => handleFieldChange('start', END_DATE)}
            />
          </div>
          <label
            htmlFor={END_DATE}
            className="absolute-label"
          >
            To
          </label>
          <div className="datetime-row">
            <DatePicker
              id={END_DATE}
              value={utcToSite(localDateRange.endDate, timezone)}
              onChange={(date) => setDate(date, 'endDate')}
              onCalendarOpen={(isOpen) => setCanSave(!isOpen)}
              onTabOrArrowBack={() => handleFieldChange('end', START_TIME_SECONDS)}
              onTextComplete={() => handleFieldChange('end', END_TIME_HOURS)}
            />
            <TimePicker
              withSeconds
              value={endTime}
              hourInputRef={endTimeHoursReference}
              onChange={(time) => setTime(time, 'endDate')}
              onTabOrArrowBack={() => handleFieldChange('end', END_DATE, 8, 10)}
              onTextComplete={() => handleFieldChange('end', null)}
            />
          </div>
        </div>
      </div>
      <hr />
      <div className="relative">
        <div className="title">Relative range</div>
        <div className="fixed-periods choices">
          {periods.map(({ label }) => (
            <div
              className={`choice ${localDateRange.label === label ? 'selected' : ''}`}
              key={label}
              role="button"
              onClick={() => handleSubmitRelativePeriod(label)}
              onKeyUp={(event) => selectRelativePeriodFromKeyboard(event, label)}
              tabIndex={0}
            >
              {label}
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const modalRoot = document.querySelector('#global-modal-container') || document.body;

  return (
    <div
      className={classes}
      ref={ref}
    >
      {headerNode}
      {escapeOverflow ? createPortal(contentNode, modalRoot) : contentNode}
    </div>
  );
};

export default withRouter(SelectPollingDateRange);
